import deliverableLabel from '../deliverableLabel';
import {
  ContentSubmission,
  ContentSubmissionKind,
  Deliverable,
  DeliverableSlot,
  DeliverableSlotsResult,
  UploadCounts,
  SlotGroup,
  SlotState,
} from '../types';

interface Input {
  deliverables: Deliverable[];
  submissions: ContentSubmission[];
  uploadCounts?: UploadCounts;
}

function toSlotState(s: ContentSubmission['aasmState']): SlotState {
  if (s === 'accepted') return 'approved';
  if (s === 'rejected') return 'edits_requested';
  if (s === 'processing') return 'processing';
  if (s === 'pending') return 'pending';
  // 'archived' submissions are filtered out upstream (active excludes them);
  // this default is purely defensive.
  return 'empty';
}

// Only determines a group's kind for label/empty-slot isMixedMedia; structured submissions carry their own kind.
function kindFromDeliverableKind(dk: string): ContentSubmissionKind {
  if (dk.includes('reel') || dk.includes('story')) return 'mixed_media';
  if (dk.includes('video')) return 'video';
  if (dk.includes('blog')) return 'blog';
  return 'photo';
}

interface GroupSpec {
  deliverableKind: string | null;
  kind: ContentSubmissionKind;
  quantity: number;
}

export default function useDeliverableSlots(
  input: Input
): DeliverableSlotsResult {
  const {deliverables, submissions, uploadCounts} = input;
  const structuredSpecs: GroupSpec[] = deliverables.map((d) => ({
    deliverableKind: d.kind,
    kind: kindFromDeliverableKind(d.kind),
    quantity: d.quantity,
  }));
  const uploadSpecs: GroupSpec[] = uploadCounts
    ? (Object.keys(uploadCounts) as ContentSubmissionKind[])
        .filter((k) => uploadCounts[k] > 0)
        .map((k) => ({
          deliverableKind: null,
          kind: k,
          quantity: uploadCounts[k],
        }))
    : [];
  // Independent sources (Ember tabs): additional deliverables must not hide videos_to_upload.
  const specs = [...uploadSpecs, ...structuredSpecs];

  const active = submissions.filter(
    (s) => s.aasmState !== 'archived' && s.parentContentSubmissionId === null
  );
  const archived = submissions.filter((s) => s.aasmState === 'archived');

  const groups: SlotGroup[] = specs.map((spec, specIndex) => {
    const matching = active
      .filter((s) =>
        spec.deliverableKind
          ? s.deliverableKind === spec.deliverableKind
          : s.deliverableKind === null && s.kind === spec.kind
      )
      .sort((a, b) => a.weight - b.weight);
    const slots: DeliverableSlot[] = [];
    for (let i = 0; i < spec.quantity; i += 1) {
      const submission = matching[i];
      slots.push({
        key: `g${specIndex}-${spec.deliverableKind ?? spec.kind}-${i}`,
        deliverableKind: spec.deliverableKind,
        kind: spec.kind,
        index: i + 1,
        isMixedMedia: (submission?.kind ?? spec.kind) === 'mixed_media',
        state: submission ? toSlotState(submission.aasmState) : 'empty',
        submission,
      });
    }
    return {
      deliverableKind: spec.deliverableKind,
      label: deliverableLabel(spec.deliverableKind, spec.kind),
      kind: spec.kind,
      requiredQuantity: spec.quantity,
      approvedCount: slots.filter((s) => s.state === 'approved').length,
      slots,
    };
  });

  const allSlots = groups.flatMap((g) => g.slots);
  return {
    groups,
    isGrouped: groups.length > 1,
    archived,
    totalRequired: groups.reduce((n, g) => n + g.requiredQuantity, 0),
    totalApproved: groups.reduce((n, g) => n + g.approvedCount, 0),
    needsActionCount: allSlots.filter(
      (s) => s.state === 'empty' || s.state === 'edits_requested'
    ).length,
  };
}
